import { memo, useState, type CSSProperties, type FocusEvent, type PointerEvent } from "react";
import { cn } from "@/utils/helpers";
import { PlateMedia, type PlateArt } from "@/app/routes/dashboard/components/Plate";
import PlatformBadge from "@/app/routes/dashboard/components/PlatformBadge";
import { Pill } from "@/app/routes/dashboard/components/ds";
import type { Trend } from "@/types/trend.type";
import PlateActions from "./PlateActions";
import {
  compactAge,
  compactTrendNum,
  originLabel,
  platformLabel,
  countedFigures,
  figuresSentence,
  topPillarFit,
  trendPlatforms,
  trendStats,
  TYPE_META,
} from "./trendMeta";
import { KitTag } from "@/app/routes/dashboard/components/kit/Tag";

export type TrendPlateSize = "tall" | "short" | "wide";

/**
 * The one place a Trend is turned into URLs for the shared plate.
 *
 * The plate itself knows nothing about trends, which is what lets References use
 * it too; this reads the preview a discovery run mirrored and hands over the two
 * links and the flag. No preview at all is the same answer as no URLs, so it
 * goes through the same resolver rather than round the side of it, and a
 * stand-in is deliberately NOT offered for it: the harness may dress a preview
 * whose URLs did not survive the trip, never a trend that never had one.
 */
/* The sentence the plate prints under "no preview captured" on THIS wall. It
   lives here rather than in the shared plate because it is a fact about trends:
   a trend is assembled from harvested examples, and opening it is what shows
   them. A reference was not assembled from anything, so its wall says something
   else. */
const NO_PREVIEW_HINT = "Open it to see what it was built from";

function TrendPlateSurface({
  trend,
  standIn,
  previewing,
}: {
  trend: Trend;
  standIn?: PlateArt;
  /** The card is under a mouse or pen, or holds visible keyboard focus. */
  previewing: boolean;
}) {
  const preview = trend.previewVideo;

  if (!preview) return <PlateMedia videoUrl={null} thumbnailUrl={null} hint={NO_PREVIEW_HINT} />;

  /* The trend's id is the plate's id, which is what lets this plate be the one
     the viewer chooses to hear: its sound chip names it, and choosing it mutes
     every other plate on the wall. */
  return (
    <PlateMedia
      videoUrl={preview.videoUrl}
      videoBroken={preview.videoBroken}
      thumbnailUrl={preview.thumbnailUrl}
      standIn={standIn}
      hint={NO_PREVIEW_HINT}
      /* A wall plate previews only while it is being pointed at or tabbed to,
         never because it scrolled into view. */
      active={previewing}
      /* The trend is what this plate shows, so a refetch that re-signs its
         URLs updates the same <video> instead of building a new one. */
      mediaId={trend.id}
      plateId={trend.id}
      plateTitle={trend.title}
    />
  );
}

/** Whether focus arrived the way a keyboard brings it. A mouse click or a tap
 *  focuses a button too, and neither of those is a request to preview. */
function isFocusVisible(target: EventTarget): boolean {
  if (!(target instanceof Element)) return false;
  try {
    return target.matches(":focus-visible");
  } catch {
    return false;
  }
}

/** One figure in the row: the value on top, its name under it. A count the run
 *  never took says so in words instead of drawing a number. */
function Figure({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="trend-fig" aria-hidden="true">
      <span className="trend-fig-value">
        {value ?? <span className="tt-label font-normal text-co-faint">Not counted</span>}
      </span>
      <span className="trend-fig-label">{label}</span>
    </div>
  );
}

/**
 * The figures row, pinned to the foot of the card so every card in a row ends
 * on the same line and every row of figures sits at the same height.
 *
 * Views and likes carry a "+" because they are FLOORS: the aggregate adds
 * nothing for an example whose count never came back. A total of zero is read
 * as NOT COUNTED, the same way every other surface reads trendStats, and never
 * as a measured zero. FOUND is when the discovery run first saw the trend.
 */
function TrendFigures({ trend }: { trend: Trend }) {
  const { views, likes } = countedFigures(trend);

  return (
    <div className="trend-figures">
      <Figure label="Views" value={views === null ? null : `${compactTrendNum(views)}+`} />
      <Figure label="Likes" value={likes === null ? null : `${compactTrendNum(likes)}+`} />
      <Figure label="Found" value={compactAge(trend.createdAt)} />
      <p className="sr-only">{figuresSentence(views, likes, trend.createdAt)}</p>
    </div>
  );
}

/**
 * One plate on the wall. The video is the object; the words sit on it, and on a
 * boosted plate they sit beside it so the 9:16 crop is never widened to fit
 * them. Every plate is portrait, because Reels and TikToks are.
 *
 * The whole card opens the trend's examples, and that is the only thing it does.
 * There is no "use in a brief" and no "send to calendar": no endpoint exists for
 * either, and a Trend carries no brandId, so a card cannot claim a trend belongs
 * to anyone's brand. The other controls sit on the plate above the hit area:
 * the sound chip, and Save and Recreate in the action bar.
 *
 * THE PLATE PREVIEWS UNDER THE POINTER. It plays, muted, while a mouse or pen
 * is over the card or the keyboard has put visible focus inside it, and holds
 * its still otherwise. A touch never starts a preview: a tap opens the trend,
 * and a clip that began playing behind the sheet would be motion nobody asked
 * for. The wall's Pause and the reduced-motion floor sit above all of this.
 *
 * The caption reads meta, title, reason, then the figures row. A field with no
 * value is omitted rather than printed as a placeholder.
 *
 * Memoised because the search box is a controlled input on the page above: every
 * keystroke re-renders the wall otherwise, and the search does not even run until
 * the form is submitted. Every prop here is referentially stable across those
 * renders, so the comparison actually holds.
 */
function TrendCard({
  trend,
  size,
  standIn,
  onOpen,
  isNew = false,
  live = true,
  fitHue,
  order,
}: {
  trend: Trend;
  size: TrendPlateSize;
  standIn?: PlateArt;
  onOpen: (id: string) => void;
  /** Created after the viewer last looked at this brand's Trends. */
  isNew?: boolean;
  /** The plate's actions may talk to the API (false in the fixture harness). */
  live?: boolean;
  /** The display tone of the fitted pillar's own hue, when it can be resolved. */
  fitHue?: string;
  /** Its place on the wall, for the domino entrance (trendWall.css). */
  order?: number;
}) {
  const { exampleCount } = trendStats(trend);
  const [hovered, setHovered] = useState(false);
  const [keyboardFocus, setKeyboardFocus] = useState(false);

  const onPointerEnter = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType !== "touch") setHovered(true);
  };
  const onFocus = (event: FocusEvent<HTMLElement>) => {
    setKeyboardFocus(isFocusVisible(event.target));
  };
  const onBlur = (event: FocusEvent<HTMLElement>) => {
    /* Moving between the chip, the actions and the card itself is still focus
       inside the card; only leaving it ends the preview. */
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setKeyboardFocus(false);
  };
  const { label: kindLabel, icon: KindIcon } = TYPE_META[trend.type];
  const origin = originLabel(trend);
  /* Ships dark: absent today, so nothing renders. Backend request:
     https://github.com/Mo7tawa/creative-os-backend/issues/91 */
  const fit = topPillarFit(trend);

  /* The sheet's rule, so the marks here and its WHERE IT WAS FOUND agree. */
  const platforms = trendPlatforms(trend) ?? [];

  return (
    <article
      className={cn(
        "trend-cell",
        size === "tall" && "trend-cell--tall",
        size === "short" && "trend-cell--short",
        size === "wide" && "trend-cell--wide",
      )}
      data-kind={trend.type}
      data-trend-id={trend.id}
      style={order === undefined ? undefined : ({ "--i": order } as CSSProperties)}
      onPointerEnter={onPointerEnter}
      onPointerLeave={() => setHovered(false)}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <div className="trend-plate">
        {/* One markup for both plate shapes. On a standard plate the media pane
            is the whole plate and the label sits over it; on a boosted plate the
            stylesheet puts the pane in one half and the label in the other, so
            the 9:16 crop survives the extra width. */}
        <div className="trend-media co-wall-media">
          <TrendPlateSurface trend={trend} standIn={standIn} previewing={hovered || keyboardFocus} />

          <div className="absolute inset-x-0 top-0 z-[3] flex items-start justify-between gap-2 p-2.5">
            {/* Top-left: what is new since the viewer last looked, then the
                editor's boost. The platform marks keep the top-right. */}
            <div className="flex flex-wrap items-start gap-1">
              {isNew ? <Pill onMedia>New</Pill> : null}
              {trend.isBoosted ? (
                // Chips on media are media glass (library Media chip / Label).
                <span className="co-media-chip co-media-chip--label tt-label uppercase">Boosted</span>
              ) : null}
            </div>
            <div className="flex flex-wrap justify-end gap-1">
              {/* The shared badge, so this wall and References cannot drift
                  apart again on what a source looks like. In media glass here,
                  because on this wall the mark sits on the video itself. */}
              {platforms.map((platform) => (
                <PlatformBadge
                  key={platform}
                  platform={platform}
                  label={platformLabel(platform)}
                  surface="media"
                />
              ))}
            </div>
          </div>

          <PlateActions trend={trend} live={live} />
        </div>

        <div className="trend-info">
          <div className="trend-caption">
            {/* Mono, because every item on this line is a token: the kind, then
                where the examples were found. The kind is an icon and a word in
                neutral ink, never a hue. Region and language print only the
                halves that carry a value, and the segment goes when neither
                does. Separators lead their own item rather than standing alone,
                so a wrap never leaves a dangling divider at the end of a line. */}
            <p className="trend-meta tt-label mb-2 flex flex-wrap items-center gap-2 uppercase text-co-muted">
              <KindIcon className="size-3 shrink-0 text-co-muted" strokeWidth={2.25} aria-hidden="true" />
              <span>{kindLabel}</span>
              {origin ? (
                <span className="text-co-faint">
                  <span aria-hidden="true">/ </span>
                  {origin}
                </span>
              ) : null}
              {/* The closest strategy pillar, as the pillar tag draws it: its
                  own hue as a 2px mark, the words in neutral ink. The hue is the
                  pillar's and only the pillar's, which is why a kind never
                  borrows one. */}
              {fit ? (
                <span className="trend-fit">
                  <span
                    className="trend-fit-mark"
                    style={{ background: fitHue ?? "var(--co-pillar-strategy)" }}
                    aria-hidden="true"
                  />
                  Fits · {fit.pillarName}
                </span>
              ) : null}
            </p>

            {/* Barlow Condensed, the display voice. The step carries its own
                leading: the base layer hands every heading a 48px line-height,
                which on a card title opens a gap wider than the block it heads. */}
            <h3
              className={cn(
                "line-clamp-3 font-extrabold uppercase text-co-text",
                size === "wide" ? "tt-title-l" : "tt-title-m",
              )}
            >
              {trend.title}
            </h3>

            {/* The reason, and only where the run wrote one. Read defensively,
                because nothing validates the feed's rows at runtime. */}
            {trend.body?.trim() ? (
              <p className={cn("tt-read-m mt-2 line-clamp-2 text-co-muted", size === "wide" && "max-w-[42ch]")}>
                {trend.body}
              </p>
            ) : null}

            {size === "wide" && trend.tags.length > 0 ? (
              <p className="mt-3 flex flex-wrap gap-1.5">
                {trend.tags.slice(0, 4).map((tag) => (
                  <KitTag key={tag}>{tag}</KitTag>
                ))}
              </p>
            ) : null}
          </div>

          <TrendFigures trend={trend} />
        </div>

        <button
          type="button"
          className="trend-hit"
          onClick={(event) => {
            /* Focus lands on the card first, without scrolling, so the panel's
               focus scope records THIS card as where to return, including in the
               browsers that do not focus a button on click. */
            event.currentTarget.focus({ preventScroll: true });
            onOpen(trend.id);
          }}
          aria-label={
            exampleCount === 0
              ? `${trend.title}: open it, no example videos came back with it`
              : `${trend.title}: open its ${exampleCount} example ${
                  exampleCount === 1 ? "video" : "videos"
                }`
          }
        />
      </div>
    </article>
  );
}

export default memo(TrendCard);
